import { useEffect } from "react";
import { useAppearance } from "./AppearanceProvider";
import { useAuthedApi } from "@/api/useAuthedApi";
import { ACCENTS, splitLegacyAccent } from "./appearance";

const isSwatch = (v: unknown): v is string =>
  typeof v === "string" && ACCENTS.some((a) => a.hex === v);

/**
 * After login, pull the user's saved per-mode ACCENTS from the server and apply
 * them. Renders nothing.
 *
 * Deliberately does NOT apply the server's color mode: light/dark/system is a
 * per-device client preference kept in localStorage (like most apps). Syncing
 * the mode from the server here is what used to revert the sidebar toggle when
 * navigating to a workspace page. Only the accents — a rare, explicit Settings
 * choice — sync across devices, via applyServerAccents (which leaves the mode).
 */
export function AppearanceSync() {
  const { setAccents } = useAppearance();
  const { profile } = useAuthedApi();

  useEffect(() => {
    let cancelled = false;
    profile
      .get()
      .then((p) => {
        if (cancelled || !p.appearance) return;
        const ap = p.appearance as { accent?: unknown; accentLight?: unknown; accentDark?: unknown };
        // Current shape: independent per-mode accents.
        if (ap.accentLight !== undefined || ap.accentDark !== undefined) {
          setAccents({
            accentLight: isSwatch(ap.accentLight) ? ap.accentLight : undefined,
            accentDark: isSwatch(ap.accentDark) ? ap.accentDark : undefined,
          });
          return;
        }
        // Legacy single accent: a custom colour carries to both modes; a plain
        // emerald (the old default) is "unset", so dark keeps its own default —
        // same split as the localStorage migration, so the two agree.
        if (isSwatch(ap.accent)) {
          setAccents(splitLegacyAccent(ap.accent));
        }
      })
      .catch(() => {
        /* offline / not signed in yet — keep the localStorage selection */
      });
    return () => {
      cancelled = true;
    };
    // profile is referentially stable (useAuthedApi memoises); run once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}
